import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import Typography from "@mui/material/Typography"

import CheckboxList from "components/Checkbox/CheckboxList"
import { ProjectFormPageProps } from "components/ProjectForm"
import ProjectFormFooter from "components/ProjectForm/components/ProjectFormFooter"
import RadioGroup from "components/RadioGroup/RadioGroup"
import { DEAL_TYPE_OPTIONS } from "components/RaiseTicketForm/config/projectFormConstants"
import { DropdownObj } from "pages/Common/commonTypes"
import TemplateCard from "./TemplateCard"
import { TEMPLATE_LOCKED_MESSAGE } from "utils/productionMovement"
import useProjectFormDealDetails from "./useProjectFormDealDetails"

import { faCircleExclamation } from "@fortawesome/sharp-solid-svg-icons"

export interface ProjectDealDetailsProps extends ProjectFormPageProps {
    handleGetTemplatePages: (templateId: number) => Promise<void>
}

const ProjectDealDetails = (props: ProjectDealDetailsProps) => {
    const { states, functions } = useProjectFormDealDetails(props)

    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                height={"400px"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                    <Typography variant="label" display={"flex"} alignItems={"center"} gap={"4px"}>
                        Specify deal type to see project template*{" "}
                        {states.formik.errors.deal_type?.title && states.formik.submitCount > 0 && (
                            <FontAwesomeIcon icon={faCircleExclamation} color="#F94054" />
                        )}
                    </Typography>
                    <RadioGroup
                        options={DEAL_TYPE_OPTIONS}
                        name="deal_type"
                        onSelect={(value: DropdownObj) => {
                            functions.handleDealTypeChange(value)
                        }}
                        value={states.deal_details_form.deal_type}
                    />
                </Box>
                {states.formik.values.deal_type.title && (
                    <>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} gap={"4px"}>
                                <Typography
                                    fontSize={"14px"}
                                    variant="label"
                                    textTransform={"none"}
                                    display={"flex"}
                                    alignItems={"center"}
                                    gap={"4px"}>
                                    Select a template:*
                                    {states.formik.errors.template_id && states.formik.submitCount > 0 && (
                                        <FontAwesomeIcon icon={faCircleExclamation} color="#F94054" />
                                    )}
                                </Typography>
                                {states.isAllSOWsLoading && (
                                    <CircularProgress
                                        size={16}
                                        sx={{
                                            marginLeft: "4px",
                                        }}
                                    />
                                )}
                            </Box>
                            <Box
                                display={"grid"}
                                gridTemplateColumns={{
                                    xs: "repeat(1, minmax(0, 1fr))",
                                    sm: "repeat(2, minmax(0, 1fr))",
                                    md: "repeat(3, minmax(0, 1fr))",
                                }}
                                gap={2}>
                                {states.templateLocked && (
                                    <Typography
                                        gridColumn="1 / -1"
                                        fontSize="12px"
                                        color="#70758D"
                                        data-testid="template-locked-note">
                                        {TEMPLATE_LOCKED_MESSAGE}: {states.formik.values.template_id?.title || "Production Planning"}
                                    </Typography>
                                )}
                                {states.sowTemplateOptions?.map(template => (
                                    <TemplateCard
                                        key={template.template_id}
                                        data={template}
                                        onChange={id => {
                                            functions.handleDealTemplateChange(id)
                                        }}
                                        checked={states.formik.values.template_id?.id === template.template_id}
                                        disabled={states.templateLocked}
                                        disabledReason={TEMPLATE_LOCKED_MESSAGE}
                                    />
                                ))}
                            </Box>
                        </Box>
                        {!states.isAllSOWsLoading && (
                            <Box display={"flex"} flexDirection={"column"} gap={1}>
                                <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                    Deal's Scope of Work
                                </Typography>
                                <CheckboxList
                                    name="deal_scope_of_work"
                                    options={states.sowOptions || []}
                                    onChange={value => {
                                        states.formik.setFieldValue("deal_scope_of_work", value)
                                    }}
                                    highlightChecked={true}
                                    disabled={true}
                                    initialValues={states.formik.values.deal_scope_of_work}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
            <ProjectFormFooter
                handleCloseForm={props.handleCloseForm}
                handleSubmit={() => states.formik.handleSubmit()}
            />

            {/* Template Change Warning Modal */}
            <Dialog
                open={states.showTemplateChangeModal}
                onClose={functions.handleCancelTemplateChange}
                aria-labelledby="template-change-dialog-title"
                aria-describedby="template-change-dialog-description">
                <DialogTitle id="template-change-dialog-title">Change Template?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="template-change-dialog-description">
                        Changing the template will delete all existing data associated with the current template. This
                        action cannot be undone. Are you sure you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={functions.handleCancelTemplateChange} color="inherit" variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={functions.handleConfirmTemplateChange}
                        color="error"
                        variant="contained"
                        disabled={states.isReconcilingTemplate}
                        autoFocus>
                        {states.isReconcilingTemplate ? <CircularProgress size={20} color="inherit" /> : "Yes, Change"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ProjectDealDetails
